/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

/* eslint-disable max-statements */

// @ts-ignore Není to ESM.
import ICAL from 'ical.js';

import {
  MagazinePlaylist,
  Playlist,
  ShufflePlaylist,
  Track
} from '@ki-wi/deterministic';
import isNil from 'lodash/isNil';
import isNumber from 'lodash/isNumber';

import IcalPlanner from '../../ical/ical-planner';
import ScheduleEnum from '../../schedule/schedule-enum';

/**
 * @import { Track as TrackModel } from '../../track/track'
 * @import { Playlist as PlaylistModel } from '../../playlist/playlist'
 * @import { SpotContent } from '../../content/controller'
 * @import { KeyedClipIteratorFactory, ClipIteratorFactory, Duration, Time, Validator as TrackValidator } from '@ki-wi/deterministic'
 * @import { Content } from '../content'
 */

/**
 * @typedef {{volume: number;}} Opts
 */

// TODO: Podle konvencí ostatních iCal věcí by tato třída měla mít závilost na ICAL.
const icalPlanner = new IcalPlanner(ICAL);

/**
 * Třída usnadňující mapování na datové modely vhodné pro Simulator.
 * @implements {KeyedClipIteratorFactory<SpotContent, string>}
 */
export class IteratorFactory {
  /**
   * Vytvoří novou instanci.
   * @param {string} id Identifikátor playlistu.
   * @param {ClipIteratorFactory<any>} itf Iterátor
   */
  constructor(id, itf) {
    this.id = id;
    this.itf = itf;
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['global']} */
  get global() {
    return this.itf.global;
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['clone']} */
  clone() {
    const itf = this.itf.clone();
    const keyedClipIteratorFactory = new IteratorFactory(this.id, itf);
    return keyedClipIteratorFactory;
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['key']} */
  key() {
    return this.id;
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['newIterator']} */
  newIterator(t) {
    return this.itf.newIterator(t);
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['valid']} */
  valid(t) {
    return this.itf.valid(t);
  }

  /** @type {KeyedClipIteratorFactory<SpotContent, string>['currentOrNextValid']} */
  currentOrNextValid(t, maxTime) {
    return this.itf.currentOrNextValid(t, maxTime);
  }

  /**
   * Funkce která namapuje Playlist na IteratorFactory.
   * @param {PlaylistModel} playlist Playlist, který se bude mapovat.
   * @param {Opts} opts
   * @returns {IteratorFactory} Namapovaný playlist do KeyedClipIteratorFactory.
   */
  static fromPlaylist(playlist, opts) {
    const { id, scope, cut, scheduleEnum } = playlist;
    // Přetypování kvůli tomu, že Track
    const tracks = playlist.tracks.map((t) =>
      IteratorFactory.fromTrack(t, opts)
    );
    /** @type {ClipIteratorFactory<any> | undefined} */
    let p;
    switch (scheduleEnum) {
      case ScheduleEnum.RANDOM:
        p = new ShufflePlaylist(...tracks);
        break;
      case ScheduleEnum.STATIC:
      case ScheduleEnum.DYNAMIC:
      default:
        p = new Playlist(...tracks);
        break;
    }

    let clipIteratorFactory = p;
    if (scope === 'application' && cut > 0) {
      clipIteratorFactory = new MagazinePlaylist(cut, p);
    }
    return new IteratorFactory(id, clipIteratorFactory);
  }

  /**
   * Funkce, která převede PlayerLighter Track model na model vhodný pro Simulator.
   * @param {TrackModel} track PlayerLighter model.
   * @param {Opts} opts TODO: vyminit
   * @returns {Track<any>} Model tracku pro Simulator.
   */
  static fromTrack(track, opts) {
    /** @type {TrackValidator | null} */
    let validator = null;
    if (!isNil(track.planIcal)) {
      validator = IteratorFactory.validator(track);
    }
    const contentVolume = track.volume * opts.volume;
    const { playlist } = track;
    let playDuration = track.playDuration;
    if (playlist && !playlist.deterministic) {
      // Rozgenerování playlistu pouze v případech kdy:
      // - top level je deterministický a vnitřní
      return trackFromTrackModelPlaylist(
        track,
        validator,
        playDuration,
        contentVolume
      );
    }

    const { medium } = track;
    // Délka media v tracku je 0, pak zkusím převzít fyzickou délku
    if (playDuration === 0 && isNumber(medium?.params?.physicalDuration)) {
      playDuration = medium.params.physicalDuration * 1000;
    }
    return trackFromTrackModelMedium(
      track,
      playDuration,
      validator,
      contentVolume
    );
  }

  /**
   * Funkce, která vytvoří validator který určí zda je track platný,
   * nebo kdy bude platný.
   * @param {TrackModel} track Track pro který se má zjišťovat validita.
   * @returns {TrackValidator} Validátor pro track.
   */
  static validator(track) {
    return {
      valid: (t) =>
        isNil(track.planIcal) ||
        icalPlanner.hasEventOnDate(track.planIcal, new Date(t / 1000)),
      currentOrNextValid: (t, maxTime) => {
        if (isNil(track.planIcal)) {
          return t;
        }
        const date = icalPlanner.currentOrNextValid(
          track.planIcal,
          new Date(t / 1000),
          new Date(maxTime / 1000)
        );
        if (isNil(date)) {
          return maxTime;
        }

        return /** @type {Time} */ (date.getTime() * 1000);
      }
    };
  }
}

/**
 * @param {Content} content
 * @param {number} duration
 * @param {TrackValidator | null} validator
 * @param {number} volume
 * @returns {Track<SpotContent>}
 */
export function trackFromTrackModelMedium(
  content,
  duration,
  validator,
  volume
) {
  return new Track(
    { model: content },
    /** @type {Duration} */
    (duration * 1000),
    volume,
    validator
  );
}

/**
 * @param {Content} content
 * @param {TrackValidator | null} validator
 * @param {number} duration
 * @param {number} volume
 * @returns {Track<IteratorFactory>}
 */
export function trackFromTrackModelPlaylist(
  content,
  validator,
  duration,
  volume
) {
  if (!content.playlist) {
    throw new Error('track should have playlist');
  }
  const iteratorFactory = IteratorFactory.fromPlaylist(content.playlist, {
    volume
  });
  return new Track(
    iteratorFactory,
    /** @type {Duration} */
    (duration * 1000),
    volume,
    validator
  );
}
